import config from '../config.js';

/**
 * Single-use tickets for the realtime sockets (ENG-498).
 *
 * The chat, stream-status and song-recommendation sockets used to carry the
 * session JWT in their URL (`?token=`), where proxies, load balancers and
 * browser history keep it. `POST /ws/tickets` (ENG-488) mints a ticket for
 * one socket type and one band instead: good for one handshake, for about 30
 * seconds. A ticket is minted right before every connect and reconnect and is
 * never reused, the way the notification stream already works (ENG-398).
 */

/** The socket types `POST /ws/tickets` accepts (streaming.Socket* in ss-backend). */
export const SOCKET_CHAT = 'chat';
export const SOCKET_STREAM_STATUS = 'stream-status';
export const SOCKET_SONG_RECOMMENDATION = 'song-recommendation';

/**
 * The ticket endpoint answered 401 or 403: the session can't open this
 * socket, and asking again won't change that. Callers stop minting (an
 * optional-auth socket connects anonymously instead; chat stops trying the
 * socket at all).
 */
export class SocketTicketRefusedError extends Error {
  constructor(status) {
    super(`socket ticket refused (HTTP ${status})`);
    this.name = 'SocketTicketRefusedError';
    this.status = status;
  }
}

/** Whether a session is stored, i.e. whether a connect should mint a ticket. */
export const hasSession = () => Boolean(sessionStorage.getItem('jwt'));

/**
 * Mint a ticket for `socket` on band `bandId`. Resolves to the ticket, or
 * rejects: with SocketTicketRefusedError on 401/403, with a plain Error on a
 * network failure, another status or a malformed answer (worth retrying).
 * @param {string} socket - one of the SOCKET_* constants
 * @param {number} bandId
 * @returns {Promise<string>}
 */
export async function mintSocketTicket(socket, bandId) {
  const jwt = sessionStorage.getItem('jwt');
  const response = await fetch(`${config.apiBaseUrl}/ws/tickets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify({ socket, bandId }),
    cache: 'no-store',
  });
  if (response.status === 401 || response.status === 403) {
    throw new SocketTicketRefusedError(response.status);
  }
  if (!response.ok) {
    throw new Error(`socket ticket: HTTP ${response.status}`);
  }
  const data = await response.json();
  if (!data || typeof data.ticket !== 'string' || data.ticket === '') {
    throw new Error('socket ticket: no ticket in the answer');
  }
  return data.ticket;
}

export const SOCKET_RETRY_INITIAL_DELAY_MS = 5000;
export const SOCKET_RETRY_MAX_DELAY_MS = 60000;

/**
 * How long to wait before the `attempt`-th socket retry while polling:
 * exponential from 5 s, capped at 60 s, with "equal jitter" (half fixed, half
 * random) so a crowd of viewers that fell back together doesn't come back
 * together.
 * @param {number} attempt - 0 for the first retry
 * @param {() => number} [random]
 */
export function socketRetryDelay(attempt, random = Math.random) {
  const ceiling = Math.min(SOCKET_RETRY_MAX_DELAY_MS, SOCKET_RETRY_INITIAL_DELAY_MS * Math.pow(2, attempt));
  return Math.round(ceiling / 2 + random() * (ceiling / 2));
}

/**
 * Retries a socket while its hook is polling (DASH-19): once a hook had given
 * up on the socket it used to poll for the rest of the mount. `schedule()`
 * arms the next retry with socketRetryDelay; a retry that comes due while the
 * tab is hidden waits until the tab is visible again, so a background tab
 * doesn't keep knocking. `reset()` (the socket opened) starts the backoff
 * over; `cancel()` drops any pending retry and the visibility listener.
 * @param {() => void} retry - called when a retry is due
 */
export function createSocketRetry(retry) {
  let attempt = 0;
  let timer = null;
  let waitingForVisible = false;

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && waitingForVisible) {
      waitingForVisible = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      retry();
    }
  };

  const fire = () => {
    timer = null;
    if (document.visibilityState === 'hidden') {
      waitingForVisible = true;
      document.addEventListener('visibilitychange', onVisibilityChange);
      return;
    }
    retry();
  };

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (waitingForVisible) {
      waitingForVisible = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  return {
    schedule() {
      cancel();
      timer = setTimeout(fire, socketRetryDelay(attempt));
      attempt += 1;
    },
    reset() {
      cancel();
      attempt = 0;
    },
    cancel,
  };
}
